import React, { useState, useEffect, useMemo } from 'react';
import api from '../api';
import InventoryExportButton from './InventoryExportButton';
import CollapsibleFilters from './CollapsibleFilters';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import {
  FormGrid,
  FormSection,
  FormActions,
  StandardInput,
  StandardSelect,
  ValidationSummary,
  EnhancedFormField,
  useFormValidation
} from './FormComponents';

interface InventoryStock {
  id: number;
  product_id: number;
  warehouse_id: number;
  quantity_on_hand: number;
  quantity_reserved: number;
  quantity_available: number;
  reorder_point: number;
  product?: {
    id: number;
    sku: string;
    name: string;
    brand?: string;
    decoration?: string;
    primary_image?: string;
    category_id?: number;
  };
  warehouse?: {
    id: number;
    name: string;
  };
}

interface SearchSuggestions {
  products: string[];
  skus: string[];
  warehouses: string[];
  brands: string[];
  categories: string[];
}

interface InventoryStockListProps {
  initialFilter?: 'all' | 'low' | 'out';
}

const InventoryStockList: React.FC<InventoryStockListProps> = ({ initialFilter = 'all' }) => {
  const [stocks, setStocks] = useState<InventoryStock[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState<SearchSuggestions>({
    products: [],
    skus: [],
    warehouses: [],
    brands: [],
    categories: []
  });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [sortField, setSortField] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [filterStatus, setFilterStatus] = useState<'all' | 'low' | 'out'>(initialFilter);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [selectedDecoration, setSelectedDecoration] = useState<string>('');
  const [selectedProductType, setSelectedProductType] = useState<string>('');
  const [decorations, setDecorations] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean; id: number | null; name: string}>({show: false, id: null, name: ''});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [showAddStockModal, setShowAddStockModal] = useState(false);
  const [exportMessage, setExportMessage] = useState<string>('');
  const [showExportMessage, setShowExportMessage] = useState(false);
  
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const importFileRef = React.useRef<HTMLInputElement>(null);

  // Reserve stock modal state
  const [showReserveModal, setShowReserveModal] = useState(false);
  const [reserveStock, setReserveStock] = useState<InventoryStock | null>(null);
  const [reserveQty, setReserveQty] = useState('');
  const [reserveReason, setReserveReason] = useState('');
  const [reserveLoading, setReserveLoading] = useState(false);

  // Add Stock form state
  const [products, setProducts] = useState<Array<{id: number; sku: string; name: string; attributes?: Array<{attribute_name: string; value: string}>}>>([]);
  const [warehouses, setWarehouses] = useState<Array<{id: number; name: string}>>([]);

  // Helper: format product label as "SKU - Name - Color - Decoration"
  const getProductLabel = (product: {sku: string; name: string; attributes?: Array<{attribute_name: string; value: string}>}) => {
    const color = product.attributes?.find(a => a.attribute_name.toLowerCase() === 'color')?.value || '';
    const decoration = product.attributes?.find(a => a.attribute_name.toLowerCase() === 'decoration')?.value || '';
    let label = `${product.sku} - ${product.name}`;
    if (color) label += ` - ${color}`;
    if (decoration) label += ` - ${decoration}`;
    return label;
  };

  const [newStock, setNewStock] = useState({
    product_id: '',
    warehouse_id: '',
    quantity_on_hand: '',
    reorder_point: '5'
  });
  const [saving, setSaving] = useState(false);
  const [stockProductSearch, setStockProductSearch] = useState('');
  const [showStockProductDropdown, setShowStockProductDropdown] = useState(false);

  // Enhanced form validation for Add Stock
  const {
    errors: validationErrors,
    touched: validationTouched,
    setValue: setValidationValue,
    setFieldTouched,
    validateForm,
    resetForm: resetValidation
  } = useFormValidation(
    {
      product_id: '',
      warehouse_id: '',
      quantity_on_hand: ''
    },
    {
      product_id: {
        required: true,
        custom: (value) => !value ? 'Please select a product' : null
      },
      warehouse_id: {
        required: true,
        custom: (value) => !value ? 'Please select a warehouse' : null
      },
      quantity_on_hand: {
        required: true,
        min: 0,
        custom: (value) => {
          const num = parseFloat(value);
          if (isNaN(num)) return 'Please enter a valid quantity';
          if (num < 0) return 'Quantity cannot be negative';
          return null;
        }
      }
    }
  );

  // Update filter when initialFilter prop changes
  useEffect(() => {
    setFilterStatus(initialFilter);
  }, [initialFilter]);

  // Fetch products and warehouses for the form
  useEffect(() => {
    if (showAddStockModal) {
      fetchProductsAndWarehouses();
    }
  }, [showAddStockModal]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchStocks();
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterStatus, selectedWarehouse, selectedBrand, selectedProductType, currentPage, pageSize]);

  // Fetch decoration options
  useEffect(() => {
    const fetchDecorations = async () => {
      try {
        const res = await api.get('/products/?limit=500');
        const prods = res.data.products || [];
        const decs = new Set<string>();
        prods.forEach((p: any) => {
          const dec = p.attributes?.find((a: any) => a.attribute_name?.toLowerCase() === 'decoration');
          if (dec?.value) decs.add(dec.value);
        });
        setDecorations(Array.from(decs).sort());
      } catch (e) {}
    };
    fetchDecorations();
  }, []);

  // Fetch search suggestions when search term changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim().length >= 2) {
        fetchSearchSuggestions();
      } else {
        setSearchSuggestions({
          products: [],
          skus: [],
          warehouses: [],
          brands: [],
          categories: []
        });
      }
    }, 200); // Faster debounce for suggestions

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const fetchStocks = async () => {
    try {
      setLoading(true);
      
      // Build search parameters
      const params: any = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize
      };

      // Add search query if provided
      if (searchTerm.trim()) {
        params.q = searchTerm.trim();
      }

      // Add filters
      if (selectedWarehouse) {
        params.warehouse_id = selectedWarehouse;
      }
      if (selectedBrand) {
        params.brand = selectedBrand;
      }
      if (selectedProductType) {
        params.product_type = selectedProductType;
      }
      if (filterStatus === 'low') {
        params.low_stock = true;
      } else if (filterStatus === 'out') {
        params.out_of_stock = true;
      }

      // Use enhanced search endpoint
      const endpoint = searchTerm.trim() || selectedBrand || selectedProductType || filterStatus !== 'all' 
        ? '/inventory/stock/search' 
        : '/inventory/stock/';

      const [stockResponse, countResponse] = await Promise.all([
        api.get(endpoint, { params }),
        api.get('/inventory/stock/search/count', { params: { ...params, skip: undefined, limit: undefined } })
      ]);

      setStocks(stockResponse.data);
      setTotalCount(countResponse.data.total_count);
    } catch (error) {
      console.error('Error fetching stock:', error);
      setStocks([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchSearchSuggestions = async () => {
    try {
      const response = await api.get('/inventory/stock/search/suggestions', {
        params: { q: searchTerm.trim(), limit: 5 }
      });
      setSearchSuggestions(response.data.suggestions);
    } catch (error) {
      console.error('Error fetching search suggestions:', error);
    }
  };

  const fetchProductsAndWarehouses = async () => {
    try {
      const [productsRes, warehousesRes] = await Promise.all([
        api.get('/products/?skip=0&limit=1000'),
        api.get('/inventory/warehouses/')
      ]);
      setProducts(productsRes.data.products || []);
      setWarehouses(warehousesRes.data || []);
    } catch (error) {
      console.error('Error fetching products/warehouses:', error);
      alert('Failed to load products and warehouses');
    }
  };

  const handleAddStock = async () => {
    // Sync validation values with form data
    setValidationValue('product_id', newStock.product_id);
    setValidationValue('warehouse_id', newStock.warehouse_id);
    setValidationValue('quantity_on_hand', newStock.quantity_on_hand);
    
    // Validate form
    if (!validateForm()) {
      // Mark all fields as touched to show errors
      setFieldTouched('product_id');
      setFieldTouched('warehouse_id');
      setFieldTouched('quantity_on_hand');
      return;
    }

    setSaving(true);
    try {
      const stockData = {
        product_id: parseInt(newStock.product_id),
        warehouse_id: parseInt(newStock.warehouse_id),
        quantity_on_hand: parseInt(newStock.quantity_on_hand),
        quantity_reserved: 0,
        reorder_point: parseInt(newStock.reorder_point) || 5,
        reorder_quantity: 50
      };

      await api.post('/inventory/stock/', stockData);
      alert('Stock record created successfully!');
      setShowAddStockModal(false);
      setNewStock({
        product_id: '',
        warehouse_id: '',
        quantity_on_hand: '',
        reorder_point: '5'
      });
      resetValidation();
      fetchStocks();
    } catch (error: any) {
      console.error('Error creating stock:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to create stock record';
      alert(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // Highlight search terms in text
  const highlightSearchTerm = (text: string, searchTerm: string) => {
    if (!searchTerm.trim() || !text) return text;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} style={{ backgroundColor: '#fef08a', padding: '0 2px' }}>
          {part}
        </mark>
      ) : part
    );
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchTerm(suggestion);
    setShowSuggestions(false);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowSuggestions(value.trim().length >= 2);
    setCurrentPage(1);
  };

  const handleSearchClear = () => {
    setSearchTerm('');
    setShowSuggestions(false);
    setCurrentPage(1);
  };

  const getStockStatus = (stock: InventoryStock) => {
    if (stock.quantity_available <= 0) {
      return <span className="status-badge status-inactive">Out of Stock</span>;
    } else if (stock.quantity_available <= stock.reorder_point) {
      return <span className="status-badge status-draft">Low Stock</span>;
    } else {
      return <span className="status-badge status-active">In Stock</span>;
    }
  };

  const handleDelete = async (id: number) => {
    const stock = stocks.find(s => s.id === id);
    setDeleteConfirm({show: true, id, name: stock?.product?.sku || `Stock #${id}`});
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.id) return;
    try {
      await api.delete(`/inventory/stock/${deleteConfirm.id}`);
      setDeleteConfirm({show: false, id: null, name: ''});
      fetchStocks();
    } catch (error: any) {
      console.error('Error deleting stock:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete stock record';
      alert(errorMsg);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(totalCount / pageSize);
  const startRecord = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endRecord = Math.min(currentPage * pageSize, totalCount);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      // Select all items on current page
      const pageIds = stocks.map(s => s.id);
      const combined = [...selectedIds, ...pageIds];
      const unique = combined.filter((id, index) => combined.indexOf(id) === index);
      setSelectedIds(unique);
    } else {
      // Deselect all items on current page
      const pageIds = stocks.map(s => s.id);
      setSelectedIds(selectedIds.filter(id => !pageIds.includes(id)));
    }
  };

  const handleSelectItem = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedIds.length > 1) {
      setShowBulkDeleteConfirm(true);
    } else if (selectedIds.length === 1) {
      handleDelete(selectedIds[0]);
      setSelectedIds([]);
    }
  };

  const confirmBulkDelete = async () => {
    if (confirmText !== 'confirm') {
      return;
    }

    const errors: string[] = [];
    for (const id of selectedIds) {
      try {
        await api.delete(`/inventory/stock/${id}`);
      } catch (error: any) {
        const errorMsg = error.response?.data?.detail || 'Failed to delete';
        errors.push(`Stock ${id}: ${errorMsg}`);
      }
    }

    if (errors.length > 0) {
      alert(`Some deletions failed:\n${errors.join('\n')}`);
    }

    setSelectedIds([]);
    setShowBulkDeleteConfirm(false);
    setConfirmText('');
    fetchStocks();
  };

  // Export handlers
  const handleExportStart = () => {
    setExportMessage('Preparing export...');
    setShowExportMessage(true);
  };

  const handleExportComplete = (filename: string) => {
    setExportMessage(`Export completed successfully! Downloaded: ${filename}`);
    setTimeout(() => setShowExportMessage(false), 5000); // Hide after 5 seconds
  };

  const handleExportError = (error: string) => {
    setExportMessage(`Export failed: ${error}`);
    setTimeout(() => setShowExportMessage(false), 8000); // Hide after 8 seconds
  };

  const handleInventoryImport = async () => {
    if (!importFile) return;
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await api.post('/inventory/import-stock', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(response.data);
      setImportFile(null);
      fetchStocks();
    } catch (error: any) {
      setImportResult({ message: 'Import failed: ' + (error.response?.data?.detail || error.message), errors: 1 });
    } finally {
      setImportLoading(false);
    }
  };

  const handleReserveSubmit = async () => {
    if (!reserveStock || !reserveQty || !reserveReason.trim()) return;
    setReserveLoading(true);
    try {
      await api.post('/inventory/reserve', {
        stock_id: reserveStock.id,
        quantity: parseInt(reserveQty),
        reason: reserveReason.trim()
      });
      setShowReserveModal(false);
      setReserveStock(null);
      setReserveQty('');
      setReserveReason('');
      fetchStocks();
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Reservation failed');
    } finally {
      setReserveLoading(false);
    }
  };

  const openReserveModal = (stock: InventoryStock) => {
    setReserveStock(stock);
    setReserveQty('');
    setReserveReason('');
    setShowReserveModal(true);
  };

  // Get unique brands for filter dropdown
  const availableBrands = useMemo(() => {
    const brands = new Set<string>();
    stocks.forEach(stock => {
      if (stock.product?.brand) {
        brands.add(stock.product.brand);
      }
    });
    return Array.from(brands).sort();
  }, [stocks]);

  const availableWarehouses = useMemo(() => {
    const warehouseMap = new Map<string, string>();
    stocks.forEach(stock => {
      if (stock.warehouse?.id && stock.warehouse?.name) {
        warehouseMap.set(stock.warehouse.id.toString(), stock.warehouse.name);
      }
    });
    return Array.from(warehouseMap.entries()).map(([id, name]) => ({ id, name }));
  }, [stocks]);

  // Product decoration map for filtering
  const [productDecorationMap, setProductDecorationMap] = useState<Record<number, string>>({});
  
  useEffect(() => {
    const fetchDecorationMap = async () => {
      try {
        const res = await api.get('/products/?limit=1000');
        const prods = res.data.products || [];
        const map: Record<number, string> = {};
        prods.forEach((p: any) => {
          const dec = p.attributes?.find((a: any) => a.attribute_name?.toLowerCase() === 'decoration');
          if (dec?.value) map[p.id] = dec.value;
        });
        setProductDecorationMap(map);
      } catch (e) {}
    };
    fetchDecorationMap();
  }, []);

  // Filter stocks by decoration and apply sorting
  const displayedStocks = useMemo(() => {
    let filtered = stocks;
    if (selectedDecoration) {
      filtered = stocks.filter(s => productDecorationMap[s.product_id] === selectedDecoration);
    }

    // Apply sorting
    return [...filtered].sort((a, b) => {
      let aVal: any = '';
      let bVal: any = '';

      switch (sortField) {
        case 'sku':
          aVal = a.product?.sku || '';
          bVal = b.product?.sku || '';
          break;
        case 'name':
          aVal = a.product?.name || '';
          bVal = b.product?.name || '';
          break;
        case 'warehouse':
          aVal = a.warehouse?.name || '';
          bVal = b.warehouse?.name || '';
          break;
        case 'decoration':
          aVal = productDecorationMap[a.product_id] || '';
          bVal = productDecorationMap[b.product_id] || '';
          break;
        case 'on_hand':
          aVal = a.quantity_on_hand || 0;
          bVal = b.quantity_on_hand || 0;
          break;
        case 'reserved':
          aVal = a.quantity_reserved || 0;
          bVal = b.quantity_reserved || 0;
          break;
        case 'available':
          aVal = a.quantity_available || 0;
          bVal = b.quantity_available || 0;
          break;
        case 'status':
          aVal = a.quantity_available <= 0 ? 0 : a.quantity_available <= a.reorder_point ? 1 : 2;
          bVal = b.quantity_available <= 0 ? 0 : b.quantity_available <= b.reorder_point ? 1 : 2;
          break;
        default:
          aVal = a.product?.name || '';
          bVal = b.product?.name || '';
      }

      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal as string).toLowerCase();
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [stocks, selectedDecoration, productDecorationMap, sortField, sortOrder]);

  // Render suggestions dropdown
  const renderSuggestions = () => {
    const allSuggestions = [
      ...searchSuggestions.products.map((s: string) => ({ text: s, type: 'Product' })),
      ...searchSuggestions.skus.map((s: string) => ({ text: s, type: 'SKU' })),
      ...searchSuggestions.warehouses.map((s: string) => ({ text: s, type: 'Warehouse' })),
      ...searchSuggestions.brands.map((s: string) => ({ text: s, type: 'Brand' })),
      ...searchSuggestions.categories.map((s: string) => ({ text: s, type: 'Category' }))
    ];

    if (allSuggestions.length === 0) return null;

    return (
      <div style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        maxHeight: '200px',
        overflowY: 'auto'
      }}>
        {allSuggestions.map((suggestion, index) => (
          <div
            key={index}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              borderBottom: index < allSuggestions.length - 1 ? '1px solid #f1f5f9' : 'none',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
            onMouseDown={() => handleSuggestionClick(suggestion.text)}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8fafc';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
          >
            <span>{suggestion.text}</span>
            <span style={{ fontSize: '12px', color: '#64748b' }}>{suggestion.type}</span>
          </div>
        ))}
      </div>
    );
  };

  if (loading && displayedStocks.length === 0) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading inventory...</div>;
  }

  return (
    <div>
      <div className="card-header" style={{ padding: '12px 16px' }}>
        <h2 className="card-title" style={{ fontSize: '16px', fontWeight: '600' }}>Inventory Stock Levels</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            type="file"
            ref={importFileRef}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) { setImportFile(f); setShowImportModal(true); } }}
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
          />
          <button className="btn btn-secondary btn-sm" onClick={() => { setShowImportModal(true); setImportFile(null); setImportResult(null); }}>
            <span className="btn-icon">📥</span>
            Import
          </button>
          <InventoryExportButton
            onExportStart={handleExportStart}
            onExportComplete={handleExportComplete}
            onExportError={handleExportError}
            className="btn btn-secondary btn-sm"
          />
          <button className="btn btn-primary btn-sm" onClick={() => { setShowAddStockModal(true); setStockProductSearch(''); }}>
            <span className="btn-icon">➕</span>
            Add to Stock
          </button>
        </div>
      </div>

      {/* Export Status Message */}
      {showExportMessage && (
        <div style={{ 
          padding: '8px 16px', 
          backgroundColor: exportMessage.includes('failed') ? '#fef2f2' : '#f0fdf4',
          borderBottom: '1px solid #e2e8f0',
          color: exportMessage.includes('failed') ? '#dc2626' : '#16a34a',
          fontSize: '13px'
        }}>
          {exportMessage}
        </div>
      )}

      {/* Compact Single-Line Filter Controls */}
      <CollapsibleFilters label="Filters">
      <div style={{ 
        padding: '12px 16px', 
        background: '#f8f9fa', 
        borderBottom: '1px solid #dee2e6',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {/* Search Input */}
          <div style={{ flex: '1', minWidth: '200px', position: 'relative' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              Search
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="SKU, product, warehouse, brand..."
                value={searchTerm}
                onChange={handleSearchChange}
                onFocus={() => setShowSuggestions(searchTerm.trim().length >= 2)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                style={{ 
                  width: '100%', 
                  padding: '6px 10px', 
                  paddingRight: searchTerm ? '30px' : '10px',
                  fontSize: '13px', 
                  border: '1px solid #ced4da', 
                  borderRadius: '4px' 
                }}
              />
              {searchTerm && (
                <button
                  onClick={handleSearchClear}
                  style={{
                    position: 'absolute',
                    right: '6px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#6c757d',
                    fontSize: '14px',
                    padding: '0 4px'
                  }}
                >
                  ✕
                </button>
              )}
              {showSuggestions && renderSuggestions()}
            </div>
          </div>
          
          {/* Warehouse Filter */}
          <div style={{ flex: '0 0 140px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              Warehouse
            </label>
            <select 
              value={selectedWarehouse}
              onChange={(e) => {
                setSelectedWarehouse(e.target.value);
                setCurrentPage(1);
              }}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px' 
              }}
            >
              <option value="">All</option>
              {availableWarehouses.map(warehouse => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* Stock Status Filter */}
          <div style={{ flex: '0 0 120px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              Stock Status
            </label>
            <select 
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value as any);
                setCurrentPage(1);
              }}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px' 
              }}
            >
              <option value="all">All</option>
              <option value="low">Low</option>
              <option value="out">Out</option>
            </select>
          </div>
          
          {/* Brand Filter */}
          <div style={{ flex: '0 0 120px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              Brand
            </label>
            <select 
              value={selectedBrand}
              onChange={(e) => {
                setSelectedBrand(e.target.value);
                setCurrentPage(1);
              }}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px' 
              }}
            >
              <option value="">All</option>
              {availableBrands.map(brand => (
                <option key={brand} value={brand}>
                  {brand}
                </option>
              ))}
            </select>
          </div>

          {/* Decoration Filter */}
          <div style={{ flex: '0 0 130px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              Decoration
            </label>
            <select 
              value={selectedDecoration}
              onChange={(e) => {
                setSelectedDecoration(e.target.value);
                setCurrentPage(1);
              }}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px' 
              }}
            >
              <option value="">All</option>
              {decorations.map(dec => (
                <option key={dec} value={dec}>{dec}</option>
              ))}
            </select>
          </div>

          {/* Product Type Filter */}
          <div style={{ flex: '0 0 140px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: '#495057', display: 'block', marginBottom: '4px' }}>
              Product Type
            </label>
            <select 
              value={selectedProductType}
              onChange={(e) => {
                setSelectedProductType(e.target.value);
                setCurrentPage(1);
              }}
              style={{ 
                width: '100%', 
                padding: '6px 10px', 
                fontSize: '13px', 
                border: '1px solid #ced4da', 
                borderRadius: '4px' 
              }}
            >
              <option value="">All</option>
              <option value="simple">Simple</option>
              <option value="variant_parent">Variant (Parent)</option>
              <option value="variant_child">Variant SKU (Child)</option>
              <option value="bundle">Bundle / Kit</option>
              <option value="customizable">Customizable</option>
              <option value="service">Service</option>
              <option value="virtual">Virtual</option>
            </select>
          </div>

          {/* Clear Filters Button */}
          {(searchTerm || selectedWarehouse || selectedBrand || selectedDecoration || selectedProductType || filterStatus !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedWarehouse('');
                setSelectedBrand('');
                setSelectedDecoration('');
                setSelectedProductType('');
                setFilterStatus('all');
                setCurrentPage(1);
              }}
              style={{ 
                padding: '6px 12px',
                fontSize: '12px',
                background: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              Clear
            </button>
          )}
          
          {/* Record Count */}
          <div style={{ 
            fontSize: '12px', 
            color: '#6c757d', 
            whiteSpace: 'nowrap',
            marginLeft: 'auto',
            padding: '6px 0'
          }}>
            {loading ? 'Searching...' : `${totalCount} records`}
          </div>
        </div>
      </div>
      </CollapsibleFilters>

      {selectedIds.length > 0 && (
        <div style={{ padding: '8px 16px', background: '#fff3cd', borderBottom: '1px solid #ffc107', fontSize: '13px' }}>
          <span>{selectedIds.length} stock records selected</span>
          <button 
            className="btn btn-danger btn-sm" 
            style={{ marginLeft: '12px', padding: '4px 10px', fontSize: '12px' }}
            onClick={handleBulkDeleteClick}
          >
            Delete Selected
          </button>
        </div>
      )}

      <div className="table-container">
        <table className="table inventory-table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <colgroup>
            <col style={{ width: '40px' }} />
            <col style={{ width: '40px' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '12%' }} />
          </colgroup>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={displayedStocks.length > 0 && displayedStocks.every(s => selectedIds.includes(s.id))}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th style={{ width: '40px' }}></th>
              <th onClick={() => handleSort('sku')} style={{ cursor: 'pointer', width: '140px' }}>
                SKU {sortField === 'sku' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', width: '180px' }}>
                Product Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('decoration')} style={{ cursor: 'pointer', width: '100px' }}>
                Decoration {sortField === 'decoration' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('on_hand')} style={{ cursor: 'pointer', textAlign: 'center' }}>
                <div>On Hand {sortField === 'on_hand' && (sortOrder === 'asc' ? '↑' : '↓')}</div>
                <div style={{ fontSize: '10px', fontWeight: 'normal', color: '#64748b' }}>Total</div>
              </th>
              <th onClick={() => handleSort('reserved')} style={{ cursor: 'pointer', textAlign: 'center' }}>
                <div>Allocated {sortField === 'reserved' && (sortOrder === 'asc' ? '↑' : '↓')}</div>
                <div style={{ fontSize: '10px', fontWeight: 'normal', color: '#64748b' }}>Reserved</div>
              </th>
              <th onClick={() => handleSort('available')} style={{ cursor: 'pointer', textAlign: 'center' }}>
                <div>Available {sortField === 'available' && (sortOrder === 'asc' ? '↑' : '↓')}</div>
                <div style={{ fontSize: '10px', fontWeight: 'normal', color: '#64748b' }}>Ready to Allocate</div>
              </th>
              <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayedStocks.length === 0 ? (
              <tr>
                <td colSpan={12} style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                  {loading ? (
                    'Loading...'
                  ) : totalCount === 0 ? (
                    searchTerm || selectedWarehouse || selectedBrand || filterStatus !== 'all' ? (
                      <div>
                        <div style={{ marginBottom: '8px' }}>No inventory records found matching your search criteria.</div>
                        <div style={{ fontSize: '14px' }}>
                          Try adjusting your search terms or filters, or{' '}
                          <button 
                            onClick={() => {
                              setSearchTerm('');
                              setSelectedWarehouse('');
                              setSelectedBrand('');
                              setFilterStatus('all');
                            }}
                            style={{ 
                              background: 'none', 
                              border: 'none', 
                              color: '#3b82f6', 
                              textDecoration: 'underline',
                              cursor: 'pointer'
                            }}
                          >
                            clear all filters
                          </button>
                        </div>
                      </div>
                    ) : (
                      'No inventory records found. Start by adding stock to your warehouses.'
                    )
                  ) : (
                    'No records match your search criteria.'
                  )}
                </td>
              </tr>
            ) : (
              displayedStocks.map(stock => {
                const rowClass = stock.quantity_available <= 0 ? 'inventory-row-out' : 
                                 stock.quantity_available <= stock.reorder_point ? 'inventory-row-low' : '';
                return (
                <tr key={stock.id} className={rowClass}>
                  <td>
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={selectedIds.includes(stock.id)}
                      onChange={(e) => handleSelectItem(stock.id, e.target.checked)}
                    />
                  </td>
                  <td style={{ padding: '6px 8px', width: '40px' }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '4px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', color: '#94a3b8', overflow: 'hidden' }}>
                      {stock.product?.primary_image ? (
                        <img src={stock.product.primary_image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : '□'}
                    </div>
                  </td>
                  <td style={{ maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {highlightSearchTerm(stock.product?.sku || '-', searchTerm)}
                  </td>
                  <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={stock.product?.name || ''}>
                    {(() => {
                      const name = stock.product?.name || `Product ${stock.product_id}`;
                      const truncated = name.length > 20 ? name.substring(0, 20) + '...' : name;
                      return highlightSearchTerm(truncated, searchTerm);
                    })()}
                  </td>
                  <td style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#475569' }}>
                    {stock.product?.decoration || '-'}
                  </td>
                  <td style={{ textAlign: 'center', color: '#475569' }}>{stock.quantity_on_hand}</td>
                  <td style={{ textAlign: 'center', color: stock.quantity_reserved > 0 ? '#f59e0b' : '#94a3b8' }}>
                    {stock.quantity_reserved > 0 ? (
                      <strong>{stock.quantity_reserved}</strong>
                    ) : (
                      stock.quantity_reserved
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <strong style={{ 
                      color: stock.quantity_available <= 0 ? '#dc2626' : 
                             stock.quantity_available <= stock.reorder_point ? '#f59e0b' : 
                             '#16a34a',
                      fontSize: '15px'
                    }}>
                      {stock.quantity_available}
                    </strong>
                  </td>
                  <td>{getStockStatus(stock)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button 
                        className="btn btn-primary" 
                        onClick={() => openReserveModal(stock)}
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                        disabled={stock.quantity_available <= 0}
                        title="Reserve stock"
                      >
                        Reserve
                      </button>
                      <button 
                        className="btn btn-danger" 
                        onClick={() => handleDelete(stock.id)}
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ); })
            )}
          </tbody>
        </table>
      </div>

      {/* Enhanced Pagination */}
      {totalCount > 0 && (
        <div className="pagination">
          <div className="pagination-info">
            Showing {startRecord} to {endRecord} of {totalCount} records
          </div>
          
          <div className="pagination-controls">
            <span>Show:</span>
            <select 
              className="page-size-selector"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            
            <button 
              className="btn btn-secondary"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </button>
            
            <span>Page {currentPage} of {totalPages}</span>
            
            <button 
              className="btn btn-secondary"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showBulkDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Confirm Bulk Delete</h3>
            <p>You are about to delete {selectedIds.length} stock records. This action cannot be undone.</p>
            <p>Type "confirm" to proceed:</p>
            <input
              type="text"
              className="form-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type confirm"
            />
            <div style={{ marginTop: '1rem', display: 'flex', gap: '8px' }}>
              <button 
                className="btn btn-danger"
                onClick={confirmBulkDelete}
                disabled={confirmText !== 'confirm'}
              >
                Delete Stock Records
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowBulkDeleteConfirm(false);
                  setConfirmText('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddStockModal && (
        <div className="modal-overlay">
          <div className={`modal ${saving ? 'form-loading' : ''}`} style={{ maxWidth: '600px' }}>
            <FormSection 
              title="Add Stock Record"
              subtitle="Create a new inventory stock record for a product in a warehouse"
            >
              <ValidationSummary 
                errors={validationErrors}
                title="Please correct the following errors before submitting:"
              />

              <FormGrid columns={1}>
                <EnhancedFormField 
                  label="Product" 
                  required 
                  tooltip="Select the product for this stock record"
                  error={validationErrors.product_id}
                  touched={validationTouched.product_id}
                  onBlur={() => setFieldTouched('product_id')}
                  helpText="Choose the product to add to inventory"
                >
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={stockProductSearch}
                      onChange={(e) => {
                        setStockProductSearch(e.target.value);
                        setShowStockProductDropdown(true);
                      }}
                      onFocus={() => setShowStockProductDropdown(true)}
                      onBlur={() => { setTimeout(() => setShowStockProductDropdown(false), 200); setFieldTouched('product_id'); }}
                      placeholder="Type SKU or product name..."
                      disabled={saving}
                      style={{ width: '100%', padding: '8px 12px', border: `1px solid ${validationErrors.product_id && validationTouched.product_id ? '#ef4444' : '#dde2e8'}`, borderRadius: '6px', fontSize: '13px' }}
                    />
                    {showStockProductDropdown && stockProductSearch.trim() && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '180px', overflow: 'auto' }}>
                        {products.filter(p => {
                          const q = stockProductSearch.toLowerCase();
                          return p.sku.toLowerCase().includes(q) || p.name.toLowerCase().includes(q);
                        }).slice(0, 15).map(p => (
                          <div
                            key={p.id}
                            onMouseDown={() => {
                              setNewStock({...newStock, product_id: String(p.id)});
                              setValidationValue('product_id', String(p.id));
                              setStockProductSearch(getProductLabel(p));
                              setShowStockProductDropdown(false);
                            }}
                            style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid #f1f5f9' }}
                            onMouseOver={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                            onMouseOut={(e) => (e.currentTarget.style.background = 'white')}
                          >
                            <span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#64748b' }}>{p.sku}</span>
                            <span style={{ marginLeft: '8px' }}>{p.name}</span>
                            {p.attributes?.find(a => a.attribute_name.toLowerCase() === 'color')?.value && (
                              <span style={{ marginLeft: '8px', color: '#64748b' }}>- {p.attributes.find(a => a.attribute_name.toLowerCase() === 'color')!.value}</span>
                            )}
                            {p.attributes?.find(a => a.attribute_name.toLowerCase() === 'decoration')?.value && (
                              <span style={{ marginLeft: '4px', color: '#64748b' }}>- {p.attributes.find(a => a.attribute_name.toLowerCase() === 'decoration')!.value}</span>
                            )}
                          </div>
                        ))}
                        {products.filter(p => {
                          const q = stockProductSearch.toLowerCase();
                          return p.sku.toLowerCase().includes(q) || p.name.toLowerCase().includes(q);
                        }).length === 0 && (
                          <div style={{ padding: '8px 12px', fontSize: '13px', color: '#94a3b8' }}>No products found</div>
                        )}
                      </div>
                    )}
                  </div>
                </EnhancedFormField>

                <EnhancedFormField 
                  label="Warehouse" 
                  required 
                  tooltip="Select the warehouse for this stock record"
                  error={validationErrors.warehouse_id}
                  touched={validationTouched.warehouse_id}
                  onBlur={() => setFieldTouched('warehouse_id')}
                  helpText="Choose the warehouse location for this stock"
                >
                  <StandardSelect
                    value={newStock.warehouse_id}
                    onChange={(e) => {
                      setNewStock({...newStock, warehouse_id: e.target.value});
                      setValidationValue('warehouse_id', e.target.value);
                    }}
                    disabled={saving}
                    error={!!validationErrors.warehouse_id}
                  >
                    <option value="">Select a warehouse...</option>
                    {warehouses.map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </option>
                    ))}
                  </StandardSelect>
                </EnhancedFormField>
              </FormGrid>

              <FormGrid columns={2}>
                <EnhancedFormField 
                  label="Quantity on Hand" 
                  required 
                  tooltip="Current quantity available in this warehouse"
                  error={validationErrors.quantity_on_hand}
                  touched={validationTouched.quantity_on_hand}
                  onBlur={() => setFieldTouched('quantity_on_hand')}
                  helpText="Enter the current stock quantity"
                >
                  <StandardInput
                    type="number"
                    value={newStock.quantity_on_hand}
                    onChange={(e) => {
                      setNewStock({...newStock, quantity_on_hand: e.target.value});
                      setValidationValue('quantity_on_hand', e.target.value);
                    }}
                    placeholder="Enter quantity"
                    min="0"
                    disabled={saving}
                    error={!!validationErrors.quantity_on_hand}
                  />
                </EnhancedFormField>

                <EnhancedFormField 
                  label="Reorder Point" 
                  tooltip="Minimum quantity before reorder alert"
                  helpText="Set the minimum stock level for reorder alerts"
                >
                  <StandardInput
                    type="number"
                    value={newStock.reorder_point}
                    onChange={(e) => setNewStock({...newStock, reorder_point: e.target.value})}
                    placeholder="Minimum quantity before reorder"
                    min="0"
                    disabled={saving}
                  />
                </EnhancedFormField>
              </FormGrid>

              <FormActions>
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddStockModal(false);
                    setNewStock({
                      product_id: '',
                      warehouse_id: '',
                      quantity_on_hand: '',
                      reorder_point: '5'
                    });
                    resetValidation();
                  }}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddStock}
                  disabled={saving}
                >
                  {saving ? 'Creating...' : 'Create Stock Record'}
                </button>
              </FormActions>
            </FormSection>
          </div>
        </div>
      )}

      {/* Inventory Import Modal */}
      {showImportModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '520px', position: 'relative' }}>
            <button
              onClick={() => { setShowImportModal(false); setImportFile(null); setImportResult(null); }}
              style={{ position: 'absolute', top: '12px', right: '16px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#64748b', lineHeight: 1 }}
              aria-label="Close"
            >×</button>
            <h3 style={{ marginBottom: '0.5rem' }}>Import Inventory Stock</h3>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '1.25rem' }}>
              Upload an Excel file with columns: <strong>SKU</strong> (required), <strong>Qty</strong> (required), <strong>Warehouse</strong> (optional).
            </p>

            <div style={{ marginBottom: '1rem' }}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={async () => {
                  try {
                    const response = await api.get('/inventory/import-stock-template', { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', 'Stock_Import_Template.xlsx');
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (error) {
                    alert('Failed to download template');
                  }
                }}
                style={{ fontSize: '12px' }}
              >
                📄 Download Template
              </button>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#334155', marginBottom: '6px' }}>
                Select Excel File
              </label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                style={{ fontSize: '13px' }}
              />
            </div>

            <div style={{ padding: '12px 16px', background: '#f0f9ff', borderRadius: '8px', marginBottom: '1rem', fontSize: '12px', color: '#64748b' }}>
              • Column names are case-insensitive<br/>
              • Updates existing stock quantities or creates new records in default warehouse<br/>
              • Products must already exist (matched by SKU)
            </div>

            {importResult && (
              <div style={{ padding: '12px 16px', borderRadius: '8px', background: importResult.errors > 0 ? '#f8d7da' : '#d4edda', border: `1px solid ${importResult.errors > 0 ? '#f5c6cb' : '#c3e6cb'}`, marginBottom: '1rem' }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: importResult.errors > 0 ? '#721c24' : '#155724', marginBottom: '4px' }}>
                  {importResult.message}
                </div>
                {importResult.failed_rows && importResult.failed_rows.length > 0 && (
                  <div style={{ marginTop: '8px', maxHeight: '150px', overflow: 'auto' }}>
                    {importResult.failed_rows.map((fr: any, idx: number) => (
                      <div key={idx} style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>
                        Row {fr.row} ({fr.sku}): {fr.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { setShowImportModal(false); setImportFile(null); setImportResult(null); }} disabled={importLoading}>
                {importResult ? 'Done' : 'Cancel'}
              </button>
              {!importResult && (
                <button className="btn btn-primary" onClick={handleInventoryImport} disabled={importLoading || !importFile}>
                  {importLoading ? 'Importing...' : 'Import Stock'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reserve Stock Modal */}
      {showReserveModal && reserveStock && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '450px' }}>
            <button className="modal-close" onClick={() => setShowReserveModal(false)} aria-label="Close">×</button>
            <h3>Reserve Inventory</h3>
            <p style={{ margin: '8px 0 16px', fontSize: '13px', color: '#64748b' }}>
              <strong>{reserveStock.product?.name}</strong> ({reserveStock.product?.sku})<br/>
              Available: <strong style={{ color: '#0d9488' }}>{reserveStock.quantity_available}</strong> units
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px', color: '#475569' }}>
                Quantity to Reserve *
              </label>
              <input
                type="number"
                value={reserveQty}
                onChange={(e) => setReserveQty(e.target.value)}
                min="1"
                max={reserveStock.quantity_available}
                placeholder="Enter quantity"
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '14px' }}
              />
              {reserveQty && parseInt(reserveQty) > reserveStock.quantity_available && (
                <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>
                  Cannot exceed available quantity ({reserveStock.quantity_available})
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, marginBottom: '6px', color: '#475569' }}>
                Reason for Reservation *
              </label>
              <textarea
                value={reserveReason}
                onChange={(e) => setReserveReason(e.target.value)}
                placeholder="e.g., Reserved for client demo, Exhibition stock, Quality hold..."
                rows={3}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowReserveModal(false)} disabled={reserveLoading}>
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleReserveSubmit}
                disabled={reserveLoading || !reserveQty || parseInt(reserveQty) <= 0 || parseInt(reserveQty) > reserveStock.quantity_available || !reserveReason.trim()}
              >
                {reserveLoading ? 'Reserving...' : 'Reserve Stock'}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDeleteModal
        isOpen={deleteConfirm.show}
        title="Delete Stock Record"
        message="Are you sure you want to delete this stock record? This action cannot be undone."
        itemName={deleteConfirm.name}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({show: false, id: null, name: ''})}
      />
    </div>
  );
};

export default InventoryStockList;
